/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Group } from '../axis.types';
import { GroupInfo } from '../../UNSAFE_Chart';
type isGroupNavigableType = (groupIndex: number) => boolean;

export function getNextGroup(
  info: GroupInfo,
  viewportEndIndex: number,
  isGroupNavigable: isGroupNavigableType
): GroupInfo | undefined {
  const groupIndex = info.groupIndex + 1;
  if (groupIndex >= viewportEndIndex) {
    return;
  }

  if (isGroupNavigable(groupIndex)) {
    return { groupIndex };
  }
  return getNextGroup({ groupIndex }, viewportEndIndex, isGroupNavigable);
}

export function getPreviousGroup(
  info: GroupInfo,
  isGroupNavigable: isGroupNavigableType
): GroupInfo | undefined {
  const groupIndex = info.groupIndex - 1;
  if (groupIndex < 0) {
    return;
  }
  if (isGroupNavigable(groupIndex)) {
    return { groupIndex };
  }
  return getPreviousGroup({ groupIndex }, isGroupNavigable);
}

export function getGroupNavUtil(
  groups: Group[],
  renderedGroups: number[],
  xIndexExtent: { startIndex: number; endIndex: number },
  drilling: 'on' | 'off' | 'groupsOnly',
  isRtl: boolean
) {
  const isGroupNavigable = (groupIndex: number) => {
    const groupsDrilling = groups[renderedGroups[groupIndex]].drilling;
    return (drilling === 'on' || drilling === 'groupsOnly') && groupsDrilling != 'off';
  };

  const getDefaultNavGroup = (info: GroupInfo) => {
    if (xIndexExtent.startIndex > renderedGroups[info.groupIndex]) {
      info.groupIndex = 0;
      return info;
    } else if (xIndexExtent.endIndex < renderedGroups[info.groupIndex]) {
      info.groupIndex = renderedGroups.length - 1;
      return info;
    }
    return;
  };

  const getNextNavGroup = (info: GroupInfo, arrow: 'ArrowRight' | 'ArrowLeft') => {
    let nextGroup = getDefaultNavGroup(info);

    if (nextGroup) return nextGroup;

    if (arrow === 'ArrowRight') {
      nextGroup = isRtl
        ? getPreviousGroup(info, isGroupNavigable)
        : getNextGroup(info, renderedGroups.length, isGroupNavigable);
    } else {
      nextGroup = isRtl
        ? getNextGroup(info, renderedGroups.length, isGroupNavigable)
        : getPreviousGroup(info, isGroupNavigable);
    }
    return nextGroup || info;
  };

  return { getDefaultNavGroup, getNextNavGroup };
}
